import GridTable from '../../table/GridTable';
import MobileAccordionTable from '../../table/MobileAccordionTable';
import {
  ColDef,
  ICellRendererParams,
  RowDoubleClickedEvent,
} from 'ag-grid-community';
import { IOrderTable, useOrderStore } from '../../../store/useOrder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import { useLoginStore } from '../../../store/useLogin';
import { useFranchiseStore } from '../../../store/useFranchise';
import OrderFixFilter from './filter/OrderFixFilter';
import queryString from 'query-string';
import Status from '../../table/Status';
import OFReloadText from './OFReloadText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getBizmMessageByCode } from '../../../util/BizResult';
import useIsMobile from '../../../hook/useIsMobile';

/**
 * @author taulmax
 * @date 2024.10.07
 * @description 주문 확정 페이지
 */
const OrderFix = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { role } = useLoginStore();
  const { orders, ordersPage, getOrderLoading, getOrders, sendPDF, promptRetry } =
    useOrderStore();

  const telRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<AgGridReact<IOrderTable>>(null);

  const isMobile = useIsMobile();

  const { myfranchise, getMyFranchise, allFranchises, getAllFranchises } = useFranchiseStore();
  const isBizProfileEnabled = useMemo(() => Boolean(myfranchise?.bizmProfileKey), [myfranchise?.bizmProfileKey]);
  useEffect(() => { getMyFranchise(); }, [getMyFranchise]);

  // 가맹점 선택 관련 state
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<string>('');
  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState(false);
  const [franchiseSearchText, setFranchiseSearchText] = useState('');

  // Admin/CS일 때 가맹점 목록 로드
  useEffect(() => {
    if (role === 'ROLE_ADMIN' || role === 'ROLE_CS') {
      getAllFranchises();
    }
  }, [role, getAllFranchises]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.franchise-dropdown-container')) {
        setShowFranchiseDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);


  const colDef: ColDef<IOrderTable>[] = useMemo(() => [
    {
      headerName: '주문 현황',
      field: 'status.description',
      cellRenderer: (props: ICellRendererParams) => {
        if (props.value) {
          return <Status status={props.value} code={props.data.status.code} />;
        }
      },
      flex: 4,
    },
    {
      headerName: '이름',
      field: 'custInfoNames',
      flex: 3,
    },
    {
      headerName: '이메일',
      field: 'email',
      flex: 4,
    },
    {
      headerName: '연락처' ,
      field: 'telNo' ,
      flex: 3,
      hide: !(role === 'ROLE_ADMIN' || role === 'ROLE_CS' || isBizProfileEnabled),
    },
    {
      headerName: '상품명',
      field: 'productName',
      flex: 3,
      valueFormatter: param => param.value?.replace('_notime', ''),
    },
    {
      headerName: '가맹점',
      field: 'franchiseId',
      flex: 3,
    },
    {
      headerName: '확정 날짜',
      field: 'confirmDt',
      valueFormatter: param => {
        if (!param.value) return '';
        return formatDate(param.value).timestamp;
      },
      flex: 4,
    },
    {
      headerName: '배치상태',
      field: 'openAiBatch.status.description',
      flex: 3,
      hide: role !== 'ROLE_ADMIN',
    },
    {
      headerName: '결과',
      field: 'resultFile.fileFullUrl',
      cellRenderer: (props: ICellRendererParams) => {
        if (props.value) {
          return (
            <button
              onClick={e => {
                e.stopPropagation();
                window.open(props.value, '_blank');
              }}
              className="!px-3 !py-1 !bg-gray-100 hover:!bg-gray-200 !text-gray-700 !text-xs !font-medium !rounded !transition-colors"
            >
              PDF
            </button>
          );
        }
      },
      flex: 3,
    },
    {
      headerName: '전송일',
      field: 'sendDt',
      valueFormatter: param => {
        if (!param.value) return '';
        return formatDate(param.value).timestamp;
      },
      flex: 3,
    },
    {
      headerName: '전송 결과',
      field: 'sendMsg',
      valueFormatter: param => {
        if (!param.value) return '';
        return getBizmMessageByCode(param.value);
      },
      flex: 3,
    },
  ], [role, isBizProfileEnabled]);

  const getFilteredData = (franchiseIdParam?: string | Record<string, any>) => {
    const telNo = telRef.current?.value || '';
    const email = emailRef.current?.value || '';
    const custName = nameRef.current?.value || '';
    const franchiseId = (typeof franchiseIdParam === 'string') ? franchiseIdParam : selectedFranchiseId;

    const parsedQuery = queryString.parse(location.search);
    const statusList = parsedQuery.statusList as string;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;
    const confirmStartDt = parsedQuery.confirmStartDt as string;
    const confirmEndDt = parsedQuery.confirmEndDt as string;

    const queryParams = {
      telNo,
      email,
      custName,
      statusList,
      size,
      confirmStartDt,
      confirmEndDt,
      franchiseId,
    };

    updateUrlWithParams(queryParams, navigate);
    getOrders({
      telNo,
      email,
      custName,
      confirmYn: 'Y',
      statusList,
      size,
      confirmStartDt,
      confirmEndDt,
      franchiseId,
    });
  };

  const onRowDoubleClicked = useCallback(
    (params: RowDoubleClickedEvent<IOrderTable>) => {
      navigate(`/order/fix/${params.data?.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const telNo = (parsedQuery.telNo as string) || '';
    const email = (parsedQuery.email as string) || '';
    const custName = (parsedQuery.custName as string) || '';
    const statusList = (parsedQuery.statusList as string) || '';
    const franchiseId = (parsedQuery.franchiseId as string) || '';
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;

    // URL에서 franchiseId 가져와서 state 동기화
    if (franchiseId !== selectedFranchiseId) {
      setSelectedFranchiseId(franchiseId);
    }

    let confirmStartDt = parsedQuery.confirmStartDt as string | undefined;
    let confirmEndDt = parsedQuery.confirmEndDt as string | undefined;

    // URL에 날짜가 없으면 기본값 (2달 전 ~ 오늘)을 URL에 반영
    if (!confirmStartDt || !confirmEndDt) {
      const today = new Date();
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(today.getMonth() - 2);

      confirmStartDt = formatDate(twoMonthsAgo)['yyyy-mm-ddTHH:mm:ss'];
      confirmEndDt = formatDate(today)['yyyy-mm-ddTHH:mm:ss'];

      // URL 파라미터에 기본 날짜값 반영
      const updatedQuery = {
        ...parsedQuery,
        confirmStartDt,
        confirmEndDt,
      };
      navigate(`?${queryString.stringify(updatedQuery)}`, { replace: true });
      return; // navigate 후 useEffect가 다시 실행되므로 여기서 return
    }

    getOrders({
      telNo,
      email,
      custName,
      confirmYn: 'Y',
      statusList,
      confirmStartDt,
      confirmEndDt,
      franchiseId,
      page,
      size,
    });
  }, [location.search, getOrders, navigate]);

  // 상품 전송 버튼
  const onClickSendPDF = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray?.length > 0) {
      sendPDF(idArray);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [sendPDF]);

  // 상품 전송 버튼
  const onClickPromptRetry = useCallback(() => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray?.length > 0) {
      promptRetry(idArray);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [promptRetry]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const updatedQuery = { ...parsedQuery, page: page.toString() };

      updateUrlWithParams(updatedQuery, navigate);
      getOrders({
        telNo: (parsedQuery.telNo as string) || '',
        email: (parsedQuery.email as string) || '',
        custName: (parsedQuery.custName as string) || '',
        statusList: (parsedQuery.statusList as string) || '',
        confirmYn: 'Y',
        confirmStartDt: parsedQuery.confirmStartDt as string,
        confirmEndDt: parsedQuery.confirmEndDt as string,
        franchiseId: (parsedQuery.franchiseId as string) || '',
        page,
        size: parseInt(parsedQuery.size as string) || 20,
      });
    },
    [location.search, navigate, getOrders]
  );

  const rowSelection = {
    mode: 'multiRow',
  };


  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !flex-col md:!flex-row !items-stretch md:!items-end !justify-between !py-2 md:!py-4 !gap-2 md:!gap-4">
        {/* 필터 영역 */}
        <div className="!flex-1">
          <OrderFixFilter
            telRef={telRef}
            emailRef={emailRef}
            nameRef={nameRef}
            getFilteredData={getFilteredData}
            selectedFranchiseId={selectedFranchiseId}
            setSelectedFranchiseId={setSelectedFranchiseId}
          />
        </div>

        {/* 회사 선택 - 데스크톱만 표시 (모바일은 필터 안에 탭으로) */}
        {(role === 'ROLE_ADMIN' || role === 'ROLE_CS') && (
          <div className="!hidden md:!block !relative franchise-dropdown-container">
            <button
              onClick={() => setShowFranchiseDropdown(!showFranchiseDropdown)}
              className={`!w-full md:!w-auto !flex !justify-between !items-center !px-3 !py-2 !rounded-lg !border !text-sm !font-medium !transition-colors ${showFranchiseDropdown ? '!bg-blue-50 !border-blue-300 !text-gray-700' : '!bg-white !border-gray-200 !text-gray-700 hover:!bg-gray-50'}`}
            >
              <span>{selectedFranchiseId ? allFranchises?.find(f => f.id === selectedFranchiseId)?.companyName || '회사 선택' : '회사 선택'}</span>
              <span className="!text-xs !text-gray-400 !ml-2">{showFranchiseDropdown ? '▲' : '▼'}</span>
            </button>

            {showFranchiseDropdown && (
              <div className="!absolute !left-0 md:!right-0 md:!left-auto !mt-2 !w-full md:!w-64 !bg-white !border !border-gray-200 !rounded-lg !shadow-lg !z-10">
                {/* 검색 입력창 */}
                <div className="!p-3 !border-b !border-gray-200">
                  <div className="!relative">
                    <input
                      type="text"
                      value={franchiseSearchText}
                      onChange={(e) => setFranchiseSearchText(e.target.value)}
                      placeholder="회사명으로 검색..."
                      className="!w-full !pl-8 !pr-3 !py-2 !text-sm !border !border-gray-300 !rounded-md focus:!outline-none focus:!ring-2 focus:!ring-blue-500 focus:!border-transparent"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <svg className="!absolute !left-2.5 !top-1/2 !transform !-translate-y-1/2 !w-4 !h-4 !text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                <div className="!max-h-60 !overflow-y-auto">
                  {allFranchises && allFranchises.length > 0 ? (
                    allFranchises
                      .filter(franchise => {
                        if (!franchiseSearchText) return true;
                        return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                      })
                      .map((franchise) => (
                        <button
                          key={franchise.id}
                          onClick={() => {
                            setSelectedFranchiseId(franchise.id);
                            setShowFranchiseDropdown(false);
                            setFranchiseSearchText('');
                            getFilteredData(franchise.id);
                          }}
                          className="!w-full !px-4 !py-2 !text-left !text-sm !text-gray-700 hover:!bg-gray-100 !transition-colors"
                        >
                          <div className="!font-medium">{franchise.companyName || ''}</div>
                          <div className="!text-xs !text-gray-500">{franchise.ceo || '-'} | {franchise.telNo || '-'}</div>
                        </button>
                      ))
                  ) : (
                    <div className="!px-4 !py-3 !text-sm !text-gray-500">회사 목록이 없습니다.</div>
                  )}
                  {allFranchises && allFranchises.filter(franchise => {
                    if (!franchiseSearchText) return true;
                    return franchise.companyName && franchise.companyName.toLowerCase().includes(franchiseSearchText.toLowerCase());
                  }).length === 0 && franchiseSearchText && (
                    <div className="!px-4 !py-3 !text-sm !text-gray-500">검색 결과가 없습니다.</div>
                  )}
                </div>
                <div className="!border-t !border-gray-200 !px-4 !py-2">
                  <button
                    onClick={() => {
                      setSelectedFranchiseId('');
                      setShowFranchiseDropdown(false);
                      setFranchiseSearchText('');
                      getFilteredData('');
                    }}
                    className="!text-sm !text-red-600 hover:!text-red-700"
                  >
                    선택 취소
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="md:!flex-1 md:!min-h-0">
        {isMobile ? (
          <MobileAccordionTable<IOrderTable>
            data={orders}
            loading={getOrderLoading}
            keyField="id"
            summaryColumns={[
              {
                field: 'status.code',
                render: (value) => {
                  const statusMap: Record<string, { label: string; color: string }> = {
                    'CONFIRM': { label: '확정', color: 'bg-gray-100 text-gray-700' },
                    'PROGRESSING': { label: '제작중', color: 'bg-blue-100 text-blue-700' },
                    'PDF_PROGRESSING': { label: '생성중', color: 'bg-yellow-100 text-yellow-700' },
                    'SUCCESS': { label: '성공', color: 'bg-green-100 text-green-700' },
                    'SEND_PENDING': { label: '전송중', color: 'bg-amber-100 text-amber-700' },
                    'SEND_FAIL': { label: '실패', color: 'bg-red-100 text-red-700' },
                    'SEND_SUCCESS': { label: '완료', color: 'bg-emerald-100 text-emerald-700' },
                  };
                  const status = statusMap[value] || { label: value, color: 'bg-gray-100 text-gray-600' };
                  return (
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${status.color}`}>
                      {status.label}
                    </span>
                  );
                },
                className: '!w-14',
              },
              {
                field: 'custInfoNames',
                render: (value) => <span className="!text-sm !font-medium">{value}</span>,
              },
              {
                field: 'productName',
                render: (value) => <span className="!text-xs !text-gray-500">{value?.replace('_notime', '')}</span>,
              },
            ]}
            detailColumns={[
              { field: 'email', label: '이메일' },
              { field: 'telNo', label: '연락처' },
              { 
                field: 'confirmDt', 
                label: '확정일',
                render: (value) => value ? formatDate(value).timestamp : '-',
              },
              { 
                field: 'resultFile.fileFullUrl', 
                label: '결과',
                render: (value) => value ? (
                  <button
                    onClick={() => window.open(value, '_blank')}
                    className="!px-2 !py-0.5 !bg-gray-100 !text-gray-700 !text-xs !rounded"
                  >
                    PDF 보기
                  </button>
                ) : '-',
              },
              { 
                field: 'sendDt', 
                label: '전송일',
                render: (value) => value ? formatDate(value).timestamp : '-',
              },
              { 
                field: 'sendMsg', 
                label: '전송결과',
                render: (value) => value ? getBizmMessageByCode(value) : '-',
              },
            ]}
            onRowClick={(row) => navigate(`/order/fix/${row.id}`)}
            emptyMessage="주문 데이터가 없습니다."
            actions={(role === 'ROLE_ADMIN' || role === 'ROLE_CS') ? (row) => (
              <>
                <button
                  onClick={() => promptRetry([row.id])}
                  className="!flex-1 !py-1.5 !text-xs !font-medium !bg-blue-500 hover:!bg-blue-600 !text-white !rounded"
                >
                  제작 재시도
                </button>
                <button
                  onClick={() => sendPDF([row.id])}
                  className="!flex-1 !py-1.5 !text-xs !font-medium !bg-blue-500 hover:!bg-blue-600 !text-white !rounded"
                >
                  상품 전송
                </button>
              </>
            ) : undefined}
          />
        ) : (
          <GridTable<IOrderTable>
            gridRef={gridRef}
            height="100%"
            isLoading={getOrderLoading}
            rowData={orders}
            columnDefs={colDef}
            getRowId={(params) => params.data?.id || ''}
            rowSelection={rowSelection}
            onRowDoubleClicked={onRowDoubleClicked}
          />
        )}
      </div>

      {/* 페이지네이션 */}
      {ordersPage && (
        <div className="!w-full !mt-4 md:!mt-6 md:!relative">
          {/* 새로고침 텍스트 (모바일: 중앙, 데스크톱: 좌측) */}
          <div className="!flex !justify-center md:!justify-start !mb-2 md:!mb-0 md:!absolute md:!left-0 md:!top-1/2 md:!-translate-y-1/2">
            <div className="!bg-blue-50 !px-3 !py-1 md:!py-1.5 !rounded-lg">
              <OFReloadText />
            </div>
          </div>

          {/* 데스크톱: 우측 버튼 영역 */}
          {(role === 'ROLE_ADMIN' || role === 'ROLE_CS') && (
            <div className="!hidden md:!flex !absolute !right-0 !top-1/2 !-translate-y-1/2 !gap-2">
              <button
                  type="button"
                  onClick={onClickPromptRetry}
                  className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
              >
                제작 재시도
              </button>
              <button
                type="button"
                onClick={onClickSendPDF}
                className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
              >
                상품 전송
              </button>
            </div>
          )}

          {ordersPage.totalPages > 1 && (
            <div className="!flex !justify-center !items-center !space-x-1 !py-3">
              {/* 맨 처음으로 */}
              <button
                onClick={() => onPageChange(0)}
                disabled={ordersPage.pageable.pageNumber === 0}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3 !-ml-1" />
              </button>
              
              {/* 이전 페이지 */}
              <button
                onClick={() => onPageChange(Math.max(0, ordersPage.pageable.pageNumber - 1))}
                disabled={ordersPage.pageable.pageNumber === 0}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              </button>
              
              {/* 페이지 번호들 */}
              <div className="!flex !space-x-0.5">
                {(() => {
                  const currentPage = ordersPage.pageable.pageNumber;
                  const totalPages = ordersPage.totalPages;
                  const maxVisible = isMobile ? 5 : 10;
                  const half = Math.floor(maxVisible / 2);
                  
                  let startPage = Math.max(0, currentPage - half);
                  let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);
                  
                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(0, endPage - maxVisible + 1);
                  }
                  
                  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                        pageNum === currentPage
                          ? '!bg-blue-600 !text-white'
                          : 'hover:!bg-gray-100 !text-gray-700'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  ));
                })()}
              </div>
              
              {/* 다음 페이지 */}
              <button
                onClick={() => onPageChange(Math.min(ordersPage.totalPages - 1, ordersPage.pageable.pageNumber + 1))}
                disabled={ordersPage.pageable.pageNumber === ordersPage.totalPages - 1}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              </button>
              
              {/* 맨 마지막으로 */}
              <button
                onClick={() => onPageChange(ordersPage.totalPages - 1)}
                disabled={ordersPage.pageable.pageNumber === ordersPage.totalPages - 1}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3 !-ml-1" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderFix;
